import { alignmentsAtom, alignmentWalkAtom } from '@/atoms/alignment.atom';
import { layersAtom } from '@/atoms/layers.atom';
import {
  stationBandsVisibleAtom,
  stationVolumeDockAtom,
  stationVolumeHoverBinAtom,
  stationVolumeSelectionAtom,
} from '@/atoms/stationVolumes.atom';
import { unitsPerPixel } from '@/lib/three/unitsPerPixel';
import type { SurfaceDifferenceMeta } from '@/components/SurfaceDifferencePanel';
import { pointAtStation } from '@/core/alignment';
import { useFeature } from '@/hooks/useFeature';
import { useStationVolumes } from '@/hooks/useStationVolumes';
import { binCentreStation, binIndexForStation, netPerMetre, symmetricDomain } from '@/lib/stationVolumes/aggregate';
import {
  bandHatchPeriodM,
  bandPresentation,
  bandSampleStep,
  binOutlinePoints,
  buildBandGeometry,
  updateBandColors,
  updateBandEmphasis,
  volumeBandColorFn,
  volumeBandCorridor,
  volumeBandPlates,
  type BandGeometry,
} from '@/lib/stationVolumes/bandGeometry';
import { createBandMaterial } from '@/lib/stationVolumes/bandMaterial';
import { formatStationRange, m3Formatter } from '@/lib/stationVolumes/format';
import { alignmentHashMatches } from '@/lib/stationVolumes/alignmentInput';
import { buildSwathOutline } from '@/lib/stationVolumes/swathGeometry';
import { SV_WALK } from '@/lib/stationVolumes/tokens';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import type { SectionOverlaysLayerMeta, StationVolumesLayerMeta } from '@/lib/stationVolumes/types';
import { Html, Line } from '@react-three/drei';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

/**
 * 3D station bands (spec §4e): one merged quad-strip mesh laid on the
 * corridor, one flat plate per station bin, coloured by net volume per metre
 * on the same diverging cut/fill ramp as the chart.
 *
 * Everything that can be reasoned about without a GPU — sampling, colour,
 * selection emphasis, the per-bin outline — lives in
 * `@/lib/stationVolumes/bandGeometry` (and the shader in `bandMaterial.ts`)
 * and is unit-tested there. This file is the r3f wiring: atoms in, one mesh +
 * the outline passes + a tooltip out.
 *
 * Only ever mounted with the dock: the bands exist to connect the chart to
 * the scene, and a corridor painted over someone's site plan after they
 * closed the panel is just clutter.
 */

/**
 * Bin separators and the corridor silhouette.
 *
 * The separator's contrast partner is the COMPOSITED band, not the terrain:
 * the line is drawn on top of a 55 %-alpha plate whose hue is either the
 * saturated cut red, the saturated fill blue, or a near-neutral at balance.
 * A near-black holds against both saturated hues and against light gravel
 * showing through the plate; white at 0.35 washes out against the cut hue and
 * disappears entirely over light gravel. Near-black loses only where a
 * near-zero (neutral, dark) band sits on dark earth — and that is precisely
 * where the band carries no signal to divide. Hence `#0b0b0a` at 0.9, not
 * `--sv-surface` (`#1a1a19`) at 0.55, which was one device pixel of almost
 * nothing.
 */
const SEPARATOR_INK = '#0b0b0a';
const SEPARATOR_OPACITY = 0.9;
/** Lift above the surface, in PIXELS. See the `useFrame` note below. */
const BAND_LIFT_PX = 2;
/** atomFamily needs a key even when no dock is open. */
const NO_LAYER = '__none';

/** The slice of a derived layer's metadata the bands read — typed, not
 *  an untyped metadata cast (ruling G5). Exported for the section overlay,
 *  which reads the very same slice (ruling D39). */
export interface DerivedSurfaceLayerMetadata {
  batchId?: string;
  /** Written by BOTH analyses from spec §10.3 on — read this first. */
  stationVolumes?: StationVolumesLayerMeta | null;
  /** Read fallback for surface-difference runs made before §10.3. */
  surfaceDifference?: SurfaceDifferenceMeta | null;
  /** What the SECTION_OVERLAYS worker last sampled for this run — absent on
   *  every run with no overlay ever configured (section-overlays spec §3). */
  sectionOverlays?: SectionOverlaysLayerMeta | null;
}

/**
 * 8 x 8 alpha tile: a 45 deg stripe that tiles seamlessly because its period
 * divides both axes. Low-coverage bins are carved by it so "we could not
 * measure this properly" survives a greyscale print, a colour-blind reader
 * and a projector (spec §5 A11y point 1).
 *
 * Exported for the 3D section overlay (spec §11.2), which hatches its cut/
 * fill ribbon with the very same tile — one hatch in the scene, not two that
 * can drift. Each consumer builds its OWN texture (the stripe pitch is set
 * per consumer through `repeat`).
 */
export function createHatchTexture(): THREE.CanvasTexture {
  const size = 8;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('StationBands: 2D canvas context unavailable for the low-coverage hatch');
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const o = (y * size + x) * 4;
      img.data[o] = 255;
      img.data[o + 1] = 255;
      img.data[o + 2] = 255;
      // Gaps are dimmed rather than punched out: the bin still has to read
      // as a band, just an untrustworthy one.
      img.data[o + 3] = ((x + y) & (size - 1)) < 3 ? 255 : 70;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  return tex;
}

/**
 * Reads the open dock's layer metadata and the matching alignment.
 *
 * The `analysis.stationVolumes` gate is applied to `dock` **first**, before
 * anything downstream reads it. The dock's Bands checkbox is the only way to
 * turn the bands on and it prompts for an upgrade instead of toggling, but the
 * visibility atom defaults to `true`, so without this a gated user would get
 * bands on the very first open — and would pull the (multi-megabyte) bins JSON
 * down to draw them. Gating `dock` collapses `layer`, `meta` and the
 * `useStationVolumes` key to null in one step, so a locked user renders nothing
 * and fetches nothing.
 *
 * Exported (ruling D39) because the 3D section overlay needs exactly this
 * chain — gate, dock, layer metadata, bins JSON, alignment — and, more to the
 * point, the same `alignmentHashMatches` verdict: the bands and the section
 * are drawn in the same frame on the same centreline, so they must not
 * disagree about whether that centreline is the one the numbers came from
 * (A8/A12).
 */
export function useBandSource() {
  const gateAllowed = useFeature('analysis.stationVolumes').allowed;
  const palette = useSvPalette();
  const openDock = useAtomValue(stationVolumeDockAtom);
  // The dock atom is shared with the clearance dock (ruling R2), so the
  // dataset check is what keeps the volumes corridor from painting underneath
  // an open clearance dock. Today the absence of `stationVolumes` metadata on a
  // clearance layer would also stop it, but only incidentally — a layer that
  // carried BOTH analyses' metadata blocks would draw the wrong bands (L4).
  const dock = gateAllowed && openDock?.dataset === 'volumes' ? openDock : null;
  const layers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);

  const layer = layers.find((l) => l.id === dock?.layerId);
  const layerMeta = (layer?.metadata ?? null) as DerivedSurfaceLayerMetadata | null;
  const meta: StationVolumesLayerMeta | null =
    layerMeta?.stationVolumes ?? layerMeta?.surfaceDifference?.stationVolumes ?? null;
  const { data: json } = useStationVolumes(dock?.layerId ?? null, meta, layerMeta?.batchId);
  const alignment = meta ? alignments.get(meta.alignment.key) : undefined;
  // Data honesty (A8): the bands are drawn on the CURRENT centreline, so when
  // the registry alignment no longer hashes to the one the run measured, the
  // corridor on screen is not the corridor the numbers came from. Same
  // predicate the dock's amber chip uses (`StationVolumeDock.tsx:234`).
  // PLAN — same mode the run was stamped in (see `StationVolumeDock`'s
  // `hashOk`); the bands and the dock must never disagree about staleness.
  const alignmentMatches = meta ? alignmentHashMatches(meta.alignment, alignment, 'PLAN') : true;
  // `meta`, `batchId`, `alignmentMatches` and `overlaysMeta` are additive for
  // the section overlay (D39); the bands themselves read only what they always
  // did. `overlaysMeta` comes off the SAME `layerMeta` object the rest of this
  // chain reads, so the scene cannot end up pointing at one run's bins and
  // another run's reference planes.
  return {
    dock,
    json,
    meta,
    overlaysMeta: layerMeta?.sectionOverlays ?? null,
    batchId: layerMeta?.batchId,
    alignment,
    alignmentMatches,
    palette,
    presentation: bandPresentation(alignmentMatches, palette),
  };
}

export const StationBands = () => {
  const { t } = useTranslation('project');
  const { dock, json, alignment, palette, presentation } = useBandSource();
  const visible = useAtomValue(stationBandsVisibleAtom(dock?.layerId ?? NO_LAYER));
  const selection = useAtomValue(stationVolumeSelectionAtom);
  const hoverBin = useAtomValue(stationVolumeHoverBinAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const walk = useAtomValue(alignmentWalkAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);

  const groupRef = useRef<THREE.Group>(null);
  /** True while the pointer is over a band — see the hover-release effect. */
  const hoveringRef = useRef(false);

  const origin = useMemo(
    () =>
      alignment
        ? new THREE.Vector3(alignment.points[0], alignment.points[1], alignment.points[2])
        : new THREE.Vector3(),
    [alignment],
  );

  // Corridor and domain are dataset-agnostic inputs to the shared geometry
  // module — computed here so `buildBandGeometry`/`updateBandColors` never
  // see a `StationBin`/`StationParams`, only the plate/corridor/colour-fn
  // seam they share with `lib/clearance/bands.ts`.
  // `json.bins` as well as `params`: an unmeasured plate on a profile-less
  // alignment (a LandXML centreline at Z = 0) stands on the run's own mean
  // measured elevation rather than dropping to the datum plane.
  const corridor = useMemo(() => (json ? volumeBandCorridor(json.params, json.bins) : null), [json]);
  const domain = useMemo(() => (json ? symmetricDomain(json.bins.map(netPerMetre)) : null), [json]);

  const built: BandGeometry | null = useMemo(
    () =>
      alignment && json && corridor
        ? buildBandGeometry(alignment, volumeBandPlates(json.bins), corridor, origin)
        : null,
    [alignment, json, corridor, origin],
  );

  useEffect(() => {
    if (!built) return;
    return () => {
      built.geometry.dispose();
      built.edges.dispose();
    };
  }, [built]);

  // Colour is NOT part of the build (see `updateBandColors`): the app can be
  // in light or dark mode and can flip between them with the corridor on
  // screen, and re-sampling 250 bins to repaint them would be absurd. A layout
  // effect, not a plain one, so the attribute is filled inside the same commit
  // the mesh is mounted in — r3f's next rAF cannot land between the two, so
  // there is no frame of black bands.
  useLayoutEffect(() => {
    if (built && json && domain) updateBandColors(built, volumeBandColorFn(json.bins, domain, palette));
  }, [built, json, domain, palette]);

  const hatchTexture = useMemo(() => createHatchTexture(), []);
  useEffect(() => () => hatchTexture.dispose(), [hatchTexture]);

  // The material is built once and then TUNED by effects. Mutating the
  // texture inside the memo made the memo a side effect in disguise —
  // fine until React decides to re-run or discard it.
  const material = useMemo(() => createBandMaterial(hatchTexture), [hatchTexture]);
  useEffect(() => () => material.dispose(), [material]);

  // `repeat` derived from the corridor's world size (ruling T14.3): the
  // stripe pitch is a fraction of the swath, so a 3 m verge and a 60 m
  // motorway corridor hatch at the same visual density. The uniform holds
  // the texture's own Matrix3, so updating it here is enough.
  useEffect(() => {
    const period = corridor ? bandHatchPeriodM(corridor) : 1;
    hatchTexture.repeat.set(1 / period, 1 / period);
    hatchTexture.updateMatrix();
  }, [hatchTexture, corridor]);

  useEffect(() => {
    material.uniforms.uOpacity.value = presentation.opacity;
  }, [material, presentation.opacity]);

  // The hover brighten mixes toward the theme's contrast ink, not toward a
  // hard-coded white: on a light-mode sky a white-ward mix bleached the band
  // instead of picking it out.
  useEffect(() => {
    const highlight = material.uniforms.uHighlight.value as THREE.Color;
    highlight.set(palette.ink);
  }, [material, palette]);

  // Brush emphasis is scoped to the dock's own layer: a stale selection from
  // a previously open dock must not dim a different run's corridor.
  const selRange = selection && dock && selection.layerId === dock.layerId ? selection : null;
  useEffect(() => {
    if (built) updateBandEmphasis(built, selRange, hoverBin);
  }, [built, selRange, hoverBin]);

  // A stale run gets the corridor it is actually drawn on outlined in amber,
  // so the mismatch is visible in the SCENE and not only as a chip in the
  // dock the user may have scrolled past.
  const staleOutline = useMemo((): THREE.Vector3[] => {
    if (!alignment || !json || !presentation.silhouette) return [];
    const geom = buildSwathOutline(
      alignment,
      json.params.swathLeftM,
      json.params.swathRightM,
      json.params.stationFromInternalM,
      json.params.stationToInternalM,
      bandSampleStep(json.params.stationIntervalM),
      origin,
      // Same stand-in the plates use, so the stale-run silhouette outlines the
      // corridor where the corridor actually is.
      { elevationM: corridor?.fallbackZM },
    );
    const pos = geom.getAttribute('position') as THREE.BufferAttribute;
    const pts: THREE.Vector3[] = [];
    for (let v = 0; v < pos.count; v++) pts.push(new THREE.Vector3(pos.getX(v), pos.getY(v), pos.getZ(v)));
    // The buffer was scratch: drei's <Line> builds its own, and a closed loop
    // just repeats the first point.
    geom.dispose();
    if (pts.length > 1) pts.push(pts[0].clone());
    return pts;
  }, [alignment, json, presentation.silhouette, origin, corridor]);

  const hoverLoop = useMemo(
    () => (built && hoverBin != null ? binOutlinePoints(built, hoverBin) : []),
    [built, hoverBin],
  );

  // Only when the walk cursor is actually inside the analysed corridor:
  // `binIndexForStation` clamps, so without the range test a cursor 2 km past
  // the last bin would light up the last bin as if it were standing on it.
  const walkBin =
    json &&
    walk &&
    walk.key === json.alignment.key &&
    walk.station >= json.params.stationFromInternalM &&
    walk.station <= json.params.stationToInternalM
      ? binIndexForStation(json.params, json.bins.length, walk.station)
      : null;
  const walkLoop = useMemo(() => (built && walkBin != null ? binOutlinePoints(built, walkBin) : []), [built, walkBin]);

  // Turning the bands off (or closing the dock) mid-hover never fires
  // `onPointerOut`, so the chart and the table would keep highlighting a bin
  // nobody is pointing at. Only this component's own hover is released — a
  // hover the chart owns must survive.
  useEffect(() => {
    if (visible && dock) return;
    if (hoveringRef.current) {
      hoveringRef.current = false;
      setHover(null);
    }
  }, [visible, dock, setHover]);

  // One precision for the whole dataset, shared with the chart, the table and
  // the PDF (ruling A15) — a row-scoped one made the 3D tooltip print `10.830`
  // for a bin the table beside it printed as `10.8`.
  const fmt = useMemo(() => m3Formatter(json?.bins ?? []), [json]);

  const tip = useMemo(() => {
    if (!json || !alignment || hoverBin == null) return null;
    const bin = json.bins[hoverBin];
    if (!bin) return null;
    const p = pointAtStation(alignment, binCentreStation(bin));
    const cut = fmt.volume(bin.cutVolumeM3);
    const fill = fmt.volume(bin.fillVolumeM3);
    const net = fmt.volume(bin.netVolumeM3);
    return {
      position: [p.x - origin.x, p.y - origin.y, (bin.zTopMeanM ?? p.z) - origin.z] as [number, number, number],
      range: formatStationRange(bin),
      cut,
      fill,
      net,
      lowCoverage: bin.lowCoverage,
    };
  }, [json, alignment, hoverBin, origin, fmt]);

  // CLAUDE.md: the lift is a SCREEN quantity, so it comes from
  // `unitsPerPixel` and never from the camera's distance to its target — the
  // ortho camera is dollied 1000 m out, which makes that distance meaningless
  // there and would put the bands 10x too high (or straight into z-fighting).
  // The band WIDTH is the real-world swath and is deliberately not scaled.
  useFrame(({ camera, size }) => {
    const g = groupRef.current;
    if (!g) return;
    // The outlines share this lift exactly: they are drawn depth-test-free
    // after the band, so ordering alone separates them. Lifting them by an
    // extra pixel of their own would only misregister them from the plate
    // they bound when the camera is oblique.
    g.position.set(origin.x, origin.y, origin.z + BAND_LIFT_PX * unitsPerPixel(camera, size, origin));
  });

  const pick = (e: ThreeEvent<PointerEvent | MouseEvent>): number | null => {
    const face = e.face;
    if (!built || !face) return null;
    const bin = built.vertexBin[face.a];
    return bin == null ? null : bin;
  };

  if (!dock || !visible || !built || !json) return null;

  return (
    <group ref={groupRef} position={origin}>
      <mesh
        geometry={built.geometry}
        material={material}
        renderOrder={2}
        onPointerMove={(e) => {
          e.stopPropagation();
          hoveringRef.current = true;
          setHover(pick(e));
        }}
        onPointerOut={() => {
          hoveringRef.current = false;
          setHover(null);
        }}
        onClick={(e) => {
          e.stopPropagation();
          // An orbit/pan that happens to start and end on a band is not a
          // click on that band; drei/r3f report the pointer travel in
          // `delta` and every other picker in this app ignores a drag.
          if (e.delta > 4) return;
          const i = pick(e);
          const bin = i == null ? null : json.bins[i];
          if (!bin) return;
          const station = binCentreStation(bin);
          // Same contract as the chart's click-to-walk: move the cursor,
          // never turn `follow` on behind the user's back.
          setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
        }}
      />
      <group>
        {/* The corridor's long edges: a hairline is right here, because the
            silhouette is continuous and the eye completes it. */}
        <lineSegments geometry={built.edges} renderOrder={3} raycast={() => null}>
          <lineBasicMaterial
            color={SEPARATOR_INK}
            transparent
            opacity={SEPARATOR_OPACITY}
            depthWrite={false}
            depthTest={false}
          />
        </lineSegments>
        {/* Bin boundaries carry the "these are discrete measurements"
            message, so they get a real 1.5 px stroke instead of one device
            pixel. `segments` batches all n+1 of them into ONE Line2. */}
        {built.capPoints.length > 1 && (
          <Line
            segments
            points={built.capPoints}
            color={SEPARATOR_INK}
            lineWidth={1.5}
            transparent
            opacity={SEPARATOR_OPACITY}
            depthWrite={false}
            depthTest={false}
            renderOrder={4}
            raycast={() => null}
          />
        )}
        {staleOutline.length > 1 && presentation.silhouette && (
          <Line
            points={staleOutline}
            color={presentation.silhouette}
            lineWidth={2}
            transparent
            opacity={0.95}
            depthWrite={false}
            depthTest={false}
            renderOrder={5}
            raycast={() => null}
          />
        )}
        {walkLoop.length > 1 && (
          <Line
            points={walkLoop}
            color={SV_WALK}
            lineWidth={2.5}
            transparent
            opacity={0.95}
            depthWrite={false}
            depthTest={false}
            renderOrder={6}
            raycast={() => null}
          />
        )}
        {hoverLoop.length > 1 && (
          <Line
            points={hoverLoop}
            color={palette.ink}
            lineWidth={2}
            transparent
            opacity={0.95}
            depthWrite={false}
            depthTest={false}
            renderOrder={7}
            raycast={() => null}
          />
        )}
      </group>
      {tip && (
        <Html
          position={tip.position}
          center
          style={{ pointerEvents: 'none', userSelect: 'none' }}
          zIndexRange={[10, 1]}>
          <div
            style={{
              transform: 'translate(-50%, calc(-100% - 12px))',
              padding: '4px 8px',
              borderRadius: 6,
              // The tooltip sits on the scene, not on the dock, so it cannot
              // use `var(--sv-*)` — it takes the same palette the bands do.
              background: `color-mix(in srgb, ${palette.surface} 92%, transparent)`,
              border: `1px solid ${palette.zero}`,
              color: palette.ink,
              fontSize: 11,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
            }}>
            <div
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontVariantNumeric: 'tabular-nums',
              }}>
              STA {tip.range}
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.92 }}>
              <span style={{ color: palette.cut }}>{t('stationVolumes.panel.cut')}</span> {tip.cut}
              <span style={{ opacity: 0.45 }}> · </span>
              <span style={{ color: palette.fill }}>{t('stationVolumes.panel.fill')}</span> {tip.fill}
              <span style={{ opacity: 0.45 }}> · </span>
              {t('stationVolumes.panel.net')} {tip.net}{' '}
              {/* The unit is stated once for the row, not once per figure. */}
              <span style={{ opacity: 0.7 }}>{t('stationVolumes.panel.units')}</span>
            </div>
            {tip.lowCoverage && <div style={{ color: palette.warn }}>{t('stationVolumes.bands.lowCoverage')}</div>}
          </div>
        </Html>
      )}
    </group>
  );
};
